import React from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  Typography
} from '@mui/material';

import {
  agreementPageSx,
  agreementCardSx,
  agreementTitleSx,
  agreementContentSx,
  agreementActionsSx
} from '../../Common/components/Styles/AuthStyles';

const AgreementView = ({
  onCancel,
  onAgree
}) => {
  const sectionTitleSx = {
    fontSize: '20px!important',
    fontWeight: 500,
    color: '#424242'
  };

  const subTitleSx = {
    fontSize: '14px!important',
    fontWeight: 500,
    color: '#424242'
  };

  return (
    <Box sx={agreementPageSx}>
      <Box sx={agreementCardSx}>
        {/* TITLE */}
        <Typography sx={agreementTitleSx}>
          Electronic Medical Records Provider Agreement
        </Typography>

        {/* SCROLLABLE CONTENT */}
        <Box sx={agreementContentSx}>
          <Typography>
            THIS IS A LEGALLY BINDING AGREEMENT between you (hereinafter “You”
            or “Provider of Record”) and EHRZip., a California corporation
            (hereinafter “We” or “Us”). You and we are collectively referred to
            as the “Parties.”
          </Typography>

          <Typography>
            BY CLICKING “I AGREE,” OR BY OTHERWISE REGISTERING FOR AN ACCOUNT,
            OR BY ACCESSING OR USING THE SERVICES (DEFINED BELOW), YOU ARE
            ENTERING INTO THIS ELECTRONIC MEDICAL RECORDS PROVIDER AGREEMENT
            (THIS “AGREEMENT”) AND YOU AGREE TO BE BOUND BY ITS TERMS AND
            CONDITIONS.
          </Typography>

          <Typography>
            Please read this Agreement carefully. Do not sign up for an account
            or use the Services if you are unwilling or unable to be bound by
            this Agreement.
          </Typography>

          {/* I. DEFINITIONS */}
          <Typography
            component="h2"
            sx={sectionTitleSx}
          >
            I. Definitions
          </Typography>

          <Typography>
            For purposes of this Agreement, the terms set forth below have the
            meanings assigned to them below. Terms not defined below or in the
            body of this Agreement (whether or not capitalized) have the
            definitions given to them in Health Insurance Portability and
            Accountability Act of 1996, and the regulations promulgated
            thereunder, including the Privacy Rule and the Security Rule, as
            amended (hereinafter “HIPPA”).
          </Typography>

          <Typography>
            “Administrative Rights” means the rights to control and direct the
            use of Provider&apos;s account, including the rights to authorize
            and limit the access rights to the Services and to Records by other
            Authorized Users.
          </Typography>

          <Typography>
            “Authorized Patient” means any patient for whom you, or an
            Authorized Worker have identified as authorized to access the
            Services.
          </Typography>

          <Typography>
            “Authorized Referral Provider” means any health care provider that
            you have identified in your account as authorized to access the
            Services, to whom You or an Authorized Worker refer a Consenting
            Patient.
          </Typography>

          <Typography>
            “Authorized User” means any You, any of your Authorized Workers,
            any Authorized Patient, and any Authorized Referral Provider who
            you have authorized to use the Services in conjunction with your
            account.
          </Typography>

          <Typography>
            “Authorized Worker” means those members of your Authorized Users
            that who you have identified in your account as authorized to
            access the Services.
          </Typography>

          <Typography>
            “Consent” means the consent to, or authorization by, an Authorized
            User of the Services allowing us to take actions described under
            this Agreement. The Consent of an Authorized User may be given by
            electronic communication to us and/or by use of the Authorized
            User&apos;s registering for an account or using the Services.
          </Typography>

          <Typography>
            “Consenting Patient” means any patient who has given their Consent
            to having their Protected Health Information, disclosed to an
            Authorized User.
          </Typography>

          <Typography>
            “Credentials” means any unique identifier, password, token,
            credential, any combination thereof, or other means we may utilize
            from time to time for authorizing access to all or any portion of
            the Services.
          </Typography>

          <Typography>
            “Privacy Rule” means the Standards for Privacy of Individually
            Identifiable Health Information at 45 CFR Part 160 and Part 164,
            Subparts A and E, as amended.
          </Typography>

          <Typography>
            “Protected Health Information” has the meaning given it in the
            Privacy Rule.
          </Typography>

          <Typography>
            “Provider” has the same meaning as “health care provider” given in
            45 CFR 160.103.
          </Typography>

          <Typography>
            “Provider of Record” the Provider in whose name your account is
            established, who is the owner of all of Your Records Authorized
            and Authorized User accounts. The Provider of Record may be changed
            in accordance with Section XIII.
          </Typography>

          <Typography>
            “Security Rule” means the Security Standards for the Protection of
            electronic Protected Health Information at 45 CFR Part 160 and
            Part 164, Subparts A and C, as amended.
          </Typography>

          <Typography>
            “Services” means our electronic health record services being
            provided hereunder.
          </Typography>

          <Typography>
            “Term” means the initial term and all renewal terms of this
            Agreement.
          </Typography>

          <Typography>
            “Worker” means the Provider of Record&apos;s employees, volunteers,
            trainees, authorized agents, and other persons whose conduct, in
            the performance of work for Provider, is under the direct control
            of such Provider, whether or not they are paid by the Provider.
          </Typography>

          <Typography>
            “Your Health Records” means and Protected Health Information that
            an Authorized User inputs, uploads, or provides with relation to
            the Services.
          </Typography>

          {/* II. THE SERVICES */}
          <Typography
            component="h2"
            sx={sectionTitleSx}
          >
            II. The Services
          </Typography>

          <Typography>
            We grant to You, as the Provider of Record, and You accept, a
            non-exclusive, personal, non-transferable (except as expressly
            permitted elsewhere herein), revocable, limited license to use any
            computer software furnished by us for access to or use of the
            Services during the Term, and a limited right to access and use the
            Services, subject to your full compliance with the terms and
            conditions set forth in this Agreement. The purpose of the Services
            is to store Your Health Records and (i) make them available to you
            and your Authorized Users for any legal purpose, including
            treatment, payment and health care operations; (ii) to facilitate
            the sharing of individuals&apos; health information among
            Authorized Users and other authorized parties, and (iii) to make
            health information available to your Authorized Patients through
            the Patient Portal. You may make Your Health Records accessible to
            other Authorized User of the Services. You authorize us, as your
            business associate, to use and disclose Your Health Records to
            Authorized Users and You are responsible for ensuring that the
            disclosure and your use of Your Health Records is consistent with
            the relevant legal restrictions and regulations. You acknowledge
            that once access rights are granted to another Authorized User, we
            have no control over the uses and disclosures that such Authorized
            User makes of Your Health Records. You further acknowledge that the
            Services only provide storage of, and access to, Your Health
            Records and that You are solely responsible for ensuring the
            accuracy and completeness of Your Health Records.
          </Typography>

          {/* III. AUTHORIZED USER ACCESS */}
          <Typography
            component="h2"
            sx={sectionTitleSx}
          >
            III. Authorized User Access to the Services
          </Typography>

          {/* A */}
          <Box sx={{pl:3,gap:1,display:'flex',flexDirection:'column'}}>
            <Typography
            component="h3"
            sx={subTitleSx}
          >
            a. Provider of Record Access
          </Typography>

          <Typography>
            The account must be established in the name of the Provider of
            Record. You agree that your use of the Services is subject to
            verification by us of your identity and credentials as a health
            care provider, and to your ongoing qualification as such. You agree
            that we may use and disclose your personal information for such
            purposes, including making inquiry of third parties concerning your
            identity and professional and practice credentials. You authorize
            such third parties to disclose to us such information as we may
            request for such purposes, and you agree to hold them and us
            harmless from any claim or liability arising from the request for
            or disclosure of such information. You agree that we may terminate
            your access to, or use of, the Services at any time if we are unable
            at any time to determine or verify your qualifications or
            credentials.
          </Typography>

          <Typography>
            Although You may delegate Administrative Rights to one or more
            Authorized Users, You remain responsible for all use of the Services
            by your Authorized Users. Although one of your Authorized
            User&apos;s may have registered for an account to which you have
            granted Administrative Rights, only You as the Provider of Record
            is entitled to any of the rights, remedies or benefits under this
            Agreement and control over access to the Administrative Rights.
          </Typography>

          <Typography>
            With your Consent, we will make Your Health Records available to
            any Authorized User you designate. You may revoke your Consent with
            respect to any other Authorized User at any time. While your
            Consent is in effect, an approved Authorized User may view, edit or
            otherwise alter any health record you have designated for his or
            her use/access, based upon the access and permissions you authorize
            for that User. If you revoke your Consent, the approved Authorized
            User may continue to have the ability to view the health record in
            the form in which it existed at the time you revoked your Consent,
            but will not be able to view changes made to the record thereafter,
            and will not be able to edit the record. You and your Authorized
            Users are fully responsible for the information in any chart that
            you share. You or your Authorized Users should not share patient
            information that violates any state or federal laws, such as
            information relating to HIV testing. In any event, but especially
            in cases of potential fraud, misuse or abuse of the Services, we
            reserve the right, in our sole judgment, to revoke, remove, cancel
            or deny continued access to any health record or the Services.
          </Typography>
          

          {/* B */}
          <Typography
            component="h3"
            sx={subTitleSx}
          >
            b. Authorized Worker Access
          </Typography>

          <Typography>
            The Provider of Record may authorize access to the Services to
            Authorized Workers who sign up for an account authorized by the
            Provider of Record. Generally Authorized Workers fall into three
            categories, administrators, authorized providers and staff.
          </Typography>

          <Typography>
            Administrators are the overseers of the Provider of Records
            account, and are responsible for managing the Authorized User
            accounts and granting or limiting access. Administrative Rights
            should be carefully controlled by the Provider of Record.
            Administrators will be given Administrative Rights only when
            authorized by the Provider of Record.
          </Typography>

          <Typography>
            Authorized providers are health care providers who will generally
            have access to view all of a patient&apos;s records and enter,
            modify, and sign SOAP notes.
          </Typography>

          <Typography>
            Authorized staff will generally have more limited access a
            patient&apos;s records and will not be able to sign off on SOAP
            notes.
          </Typography>

          <Typography>
            It is the responsibility of the Provider of Record to promptly
            terminate authorization and access when an Authorized Worker is no
            longer supposed to have access to the Services.
          </Typography>

          {/* C */}
          <Typography
            component="h3"
            sx={subTitleSx}
          >
            c. Authorized Referral Provider Access
          </Typography>

          <Typography>
            The Provider of Record may authorize temporary access to the
            Services to Authorized Referral Providers through a web-based
            referral portal (a “Referral Portal”). Authorized Referral
            Providers must be expressly authorized by the Provider of Record
            and must have their own access account. Such access may not include
            Administrative Rights and Referral Providers may not be permitted
            to or modify the Provider of Record&apos;s patient records. You are
            solely responsible for the information that you make available
            through a Referral Portal.
          </Typography>
          </Box>
        </Box>

        {/* FIXED ACTIONS */}
        <Box sx={agreementActionsSx}>
          <Button
            onClick={onCancel}
            variant="outlined"
          >
            Cancel
          </Button>

          <Button
            onClick={onAgree}
            variant="contained"
          >
            Agree
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

AgreementView.propTypes = {
  onCancel: PropTypes.func.isRequired,
  onAgree: PropTypes.func.isRequired
};

export default AgreementView;